import { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { STATUS_STYLES } from "@/lib/constants";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import {
  Plus, Search, Loader2, Phone, Mail, MapPin, BadgeCheck, Clock, Users,
  Trash2, Pencil, Briefcase, History, Star,
} from "lucide-react";

const CATEGORIES = ["Plomberie", "Électricité", "Carrelage", "Peinture", "Maçonnerie", "Menuiserie", "Rénovation salle de bain", "Rénovation intérieure", "Autre"];

function VerifBadge({ status }) {
  const ok = status === "Vérifié";
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${ok ? "bg-sky-100 text-sky-800 border-sky-300" : "bg-yellow-100 text-yellow-800 border-yellow-300"}`}>
      {ok ? <BadgeCheck className="h-3 w-3" /> : <Clock className="h-3 w-3" />} {status}
    </span>
  );
}

export default function Artisans() {
  const [artisans, setArtisans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [historyArtisan, setHistoryArtisan] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (filter !== "all") params.verification_status = filter;
      const { data } = await api.get("/artisans", { params });
      setArtisans(data);
    } finally {
      setLoading(false);
    }
  }, [search, filter]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const remove = async (id) => {
    try {
      await api.delete(`/artisans/${id}`);
      toast.success("Artisan supprimé");
      load();
    } catch (e) {
      toast.error("Suppression impossible");
    }
  };

  return (
    <div className="p-5 sm:p-8 max-w-7xl mx-auto" data-testid="artisans-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Gestion des artisans</h1>
          <p className="text-slate-500 mt-1">{artisans.length} artisan(s) dans le réseau</p>
        </div>
        <Button onClick={() => { setEditing(null); setDialogOpen(true); }} className="bg-blue-600 hover:bg-blue-700" data-testid="add-artisan-button">
          <Plus className="h-4 w-4 mr-2" /> Ajouter un artisan
        </Button>
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-3 bg-white rounded-xl border border-slate-200 p-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input className="pl-9" placeholder="Rechercher par nom, ville, métier..." value={search} onChange={(e) => setSearch(e.target.value)} data-testid="artisan-filter-input" />
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-[180px]" data-testid="artisan-verif-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les statuts</SelectItem>
            <SelectItem value="Vérifié">Vérifié</SelectItem>
            <SelectItem value="En attente">En attente</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="grid place-items-center py-32"><Loader2 className="h-8 w-8 animate-spin text-blue-600" /></div>
      ) : artisans.length === 0 ? (
        <div className="mt-10 text-center py-20 bg-white rounded-2xl border border-slate-200">
          <Users className="h-10 w-10 mx-auto text-slate-300" />
          <p className="mt-3 text-slate-500">Aucun artisan trouvé.</p>
        </div>
      ) : (
        <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="artisan-list-table">
          {artisans.map((a) => (
            <div key={a.id} className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm flex flex-col" data-testid={`artisan-card-${a.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="h-11 w-11 rounded-xl bg-[#0B1E4D] grid place-items-center text-white font-display font-bold">
                  {a.name.charAt(0)}
                </div>
                <VerifBadge status={a.verification_status} />
              </div>
              <h3 className="font-display font-semibold text-slate-900 mt-3">{a.name}</h3>
              <div className="flex flex-wrap gap-1 mt-2">
                {(a.trades || []).map((t) => <span key={t} className="text-xs bg-blue-50 text-blue-700 rounded-md px-2 py-0.5">{t}</span>)}
              </div>
              <div className="mt-3 space-y-1.5 text-sm text-slate-600">
                <div className="flex items-center gap-2"><MapPin className="h-3.5 w-3.5 text-slate-400" /> {a.city}</div>
                <div className="flex items-center gap-2"><Phone className="h-3.5 w-3.5 text-slate-400" /> {a.phone}</div>
                {a.email ? <div className="flex items-center gap-2 truncate"><Mail className="h-3.5 w-3.5 text-slate-400 shrink-0" /> <span className="truncate">{a.email}</span></div> : null}
              </div>
              <div className="mt-3 flex items-center gap-3 text-xs text-slate-500">
                <span className="inline-flex items-center gap-1"><Briefcase className="h-3.5 w-3.5" /> {a.completed_projects} terminé(s)</span>
                <span>· {a.total_projects} total</span>
              </div>
              <div className="mt-2">
                {a.avg_rating ? (
                  <div className="inline-flex items-center gap-1.5 rounded-lg bg-amber-50 border border-amber-200 px-2.5 py-1" data-testid={`artisan-rating-${a.id}`}>
                    <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
                    <span className="font-mono-nums text-sm font-bold text-amber-900">{a.avg_rating}</span>
                    <span className="text-xs text-amber-700">({a.rating_count} avis)</span>
                  </div>
                ) : (
                  <span className="text-xs text-slate-400 italic">Pas encore de note client</span>
                )}
              </div>
              <div className="mt-4 pt-4 border-t border-slate-100 flex items-center gap-2">
                <Button variant="outline" size="sm" className="flex-1" onClick={() => setHistoryArtisan(a)} data-testid={`artisan-history-${a.id}`}>
                  <History className="h-3.5 w-3.5 mr-1.5" /> Historique
                </Button>
                <Button variant="ghost" size="icon" onClick={() => { setEditing(a); setDialogOpen(true); }} data-testid={`artisan-edit-${a.id}`}>
                  <Pencil className="h-4 w-4 text-slate-500" />
                </Button>
                <Button variant="ghost" size="icon" onClick={() => remove(a.id)} data-testid={`artisan-delete-${a.id}`}>
                  <Trash2 className="h-4 w-4 text-rose-500" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <ArtisanDialog open={dialogOpen} onOpenChange={setDialogOpen} artisan={editing} onSaved={load} />
      <ArtisanHistory artisan={historyArtisan} onClose={() => setHistoryArtisan(null)} />
    </div>
  );
}

function ArtisanDialog({ open, onOpenChange, artisan, onSaved }) {
  const [form, setForm] = useState({ name: "", trades: [], city: "", phone: "", email: "", verification_status: "En attente" });
  const [saving, setSaving] = useState(false);
  const [tradeInput, setTradeInput] = useState("");

  useEffect(() => {
    if (open) {
      setForm(artisan
        ? { name: artisan.name, trades: artisan.trades || [], city: artisan.city, phone: artisan.phone, email: artisan.email || "", verification_status: artisan.verification_status }
        : { name: "", trades: [], city: "", phone: "", email: "", verification_status: "En attente" });
    }
  }, [open, artisan]);

  const toggleTrade = (t) => setForm((f) => ({ ...f, trades: f.trades.includes(t) ? f.trades.filter((x) => x !== t) : [...f.trades, t] }));
  const addTrade = (val) => {
    const t = (val || "").trim();
    if (!t) return;
    if (!form.trades.includes(t)) setForm((f) => ({ ...f, trades: [...f.trades, t] }));
    setTradeInput("");
  };

  const save = async () => {
    if (!form.name || !form.city || !form.phone) {
      toast.error("Nom, ville et téléphone sont obligatoires");
      return;
    }
    setSaving(true);
    try {
      if (artisan) {
        await api.patch(`/artisans/${artisan.id}`, form);
        toast.success("Artisan mis à jour");
      } else {
        await api.post("/artisans", form);
        toast.success("Artisan ajouté");
      }
      onOpenChange(false);
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="artisan-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">{artisan ? "Modifier l'artisan" : "Nouvel artisan"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label className="text-sm">Nom / Société *</Label>
            <Input className="mt-1.5" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="artisan-name-input" />
          </div>
          <div>
            <Label className="text-sm">Métier(s)</Label>
            {form.trades.length > 0 && (
              <div className="mt-1.5 flex flex-wrap gap-2">
                {form.trades.map((t) => (
                  <span key={t} className="inline-flex items-center gap-1 rounded-full bg-blue-600 text-white text-xs px-3 py-1.5" data-testid={`trade-chip-${t}`}>
                    {t}
                    <button type="button" onClick={() => toggleTrade(t)} className="hover:text-blue-200 font-bold leading-none">×</button>
                  </span>
                ))}
              </div>
            )}
            <div className="mt-2 flex gap-2">
              <Input
                value={tradeInput}
                onChange={(e) => setTradeInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTrade(tradeInput); } }}
                placeholder="Saisir un métier (ex: Couvreur, Plaquiste, Paysagiste...)"
                data-testid="artisan-trade-input"
              />
              <Button type="button" variant="outline" onClick={() => addTrade(tradeInput)} data-testid="artisan-trade-add">Ajouter</Button>
            </div>
            <p className="mt-2 text-xs text-slate-400">Suggestions rapides :</p>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {CATEGORIES.map((c) => (
                <button key={c} type="button" onClick={() => addTrade(c)}
                  className="text-xs rounded-full border border-slate-300 text-slate-600 hover:border-blue-400 hover:text-blue-600 px-2.5 py-1 transition-colors">
                  + {c}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm">Ville *</Label>
              <Input className="mt-1.5" value={form.city} onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))} data-testid="artisan-city-input" />
            </div>
            <div>
              <Label className="text-sm">Téléphone *</Label>
              <Input className="mt-1.5" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} data-testid="artisan-phone-input" />
            </div>
          </div>
          <div>
            <Label className="text-sm">Email</Label>
            <Input className="mt-1.5" type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} data-testid="artisan-email-input" />
          </div>
          <div>
            <Label className="text-sm">Statut de vérification</Label>
            <Select value={form.verification_status} onValueChange={(v) => setForm((f) => ({ ...f, verification_status: v }))}>
              <SelectTrigger className="mt-1.5" data-testid="artisan-verif-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Vérifié">Vérifié</SelectItem>
                <SelectItem value="En attente">En attente</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="artisan-save-button">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enregistrer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ArtisanHistory({ artisan, onClose }) {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (artisan) {
      setLoading(true);
      api.get(`/artisans/${artisan.id}/history`).then((r) => setProjects(r.data)).finally(() => setLoading(false));
    }
  }, [artisan]);

  if (!artisan) return null;

  return (
    <Sheet open={!!artisan} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="artisan-history-panel">
        <SheetHeader>
          <SheetTitle className="font-display text-left">Chantiers de {artisan.name}</SheetTitle>
        </SheetHeader>
        <div className="mt-6 space-y-3">
          {loading ? (
            <div className="grid place-items-center py-20"><Loader2 className="h-6 w-6 animate-spin text-blue-600" /></div>
          ) : projects.length === 0 ? (
            <p className="text-slate-500 text-sm text-center py-10">Aucun chantier assigné pour le moment.</p>
          ) : (
            projects.map((p) => (
              <div key={p.id} className="rounded-xl border border-slate-200 p-4">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono-nums text-xs font-bold text-slate-400">{p.reference}</span>
                  <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${STATUS_STYLES[p.status] || ""}`}>{p.status}</span>
                </div>
                <p className="font-medium text-slate-900 mt-1">{p.category} · {p.city}</p>
                <p className="text-sm text-slate-500">{p.full_name}</p>
                {p.amount_ht ? <p className="text-sm text-slate-600 mt-1 font-mono-nums">Montant HT : {p.amount_ht.toLocaleString("fr-FR")} €</p> : null}
              </div>
            ))
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
